import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Star, Eye, EyeOff, Archive, Search } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput } from "@/components/admin/ui";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u && u.startsWith("/api/")) ? BACKEND + u : u;

export default function AdminGallery() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = () => api.get(`/admin/gallery${cat ? `?category=${encodeURIComponent(cat)}` : ""}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f); fd.append("category", cat || "Property Details"); fd.append("title", f.name);
      try {
        const res = await fetch(`${BACKEND}/api/admin/gallery/upload`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("thed_token")}` }, body: fd });
        if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Upload failed"); }
        else toast.success(`Uploaded ${f.name}`);
      } catch (e) { toast.error("Upload failed"); }
    }
    setBusy(false); load();
  };
  const update = async (id, patch) => { try { await api.put(`/admin/gallery/${id}`, patch); load(); } catch (e) { toast.error(apiErr(e)); } };
  const bulk = async (action) => { if (!sel.length) { toast.error("Select images first."); return; } const category = action === "categorize" ? prompt("New category:") : undefined; try { await api.post("/admin/gallery/bulk", { ids: sel, action, category }); toast.success(`Bulk ${action}: ${sel.length}`); setSel([]); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  if (data === null) return <Loading />;
  if (!data) return <p className="text-muted-foreground">Unable to load gallery.</p>;
  let imgs = data.images;
  if (search) { const s = search.toLowerCase(); imgs = imgs.filter((i) => (i.title + i.caption + i.category).toLowerCase().includes(s)); }

  return (
    <div>
      <AdminHeading title="Gallery" subtitle="Property media administration. Supplied The D photography is the approved initial media.">
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files))} data-testid="gallery-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="gallery-upload-btn"><Upload size={15} /> {busy ? "Uploading…" : "Upload"}</button>
        </div>
      </AdminHeading>

      <div className={`mb-4 text-xs px-3 py-2 rounded-lg border ${data.storage.production_configured ? "border-pool/30 text-pool bg-pool/5" : "border-gold/30 text-gold bg-gold/5"}`} data-testid="storage-status">
        Storage: local development provider active · <b>{data.storage.production_label}</b>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <select className={adminInput + " w-auto"} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="gallery-category"><option value="">All categories</option>{data.categories.map((c) => <option key={c}>{c}</option>)}</select>
        <div className="relative"><Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" /><input className={adminInput + " pl-7"} placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="gallery-search" /></div>
        {sel.length > 0 && (
          <div className="flex gap-1 items-center text-xs" data-testid="gallery-bulk-bar">
            <span className="text-gold">{sel.length} selected</span>
            <button onClick={() => bulk("publish")} className="btn-ghost-gold px-2 py-1">Publish</button>
            <button onClick={() => bulk("unpublish")} className="btn-ghost-gold px-2 py-1">Unpublish</button>
            <button onClick={() => bulk("categorize")} className="btn-ghost-gold px-2 py-1">Categorize</button>
            <button onClick={() => bulk("archive")} className="btn-ghost-gold px-2 py-1">Archive</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {imgs.map((g) => (
          <div key={g.id} className={`rounded-lg overflow-hidden border ${sel.includes(g.id) ? "border-gold" : "border-white/10"} bg-surface/60`} data-testid={`gallery-item-${g.id}`}>
            <div className="relative aspect-video bg-black/40">
              <img src={mediaUrl(g.thumbnail_url || g.url)} alt={g.alt_text} className="w-full h-full object-cover" />
              <input type="checkbox" checked={sel.includes(g.id)} onChange={() => toggleSel(g.id)} className="absolute top-2 left-2 accent-gold w-4 h-4" data-testid={`gallery-select-${g.id}`} />
              {g.featured && <Star size={16} className="absolute top-2 right-2 text-gold fill-gold" />}
            </div>
            <div className="p-3">
              <input className="bg-transparent text-sm w-full font-medium mb-1 border-b border-transparent focus:border-white/20 outline-none" defaultValue={g.title} onBlur={(e) => e.target.value !== g.title && update(g.id, { title: e.target.value })} data-testid={`gallery-title-${g.id}`} />
              <input className="bg-transparent text-xs w-full text-muted-foreground mb-2 outline-none" defaultValue={g.caption} placeholder="Caption" onBlur={(e) => e.target.value !== g.caption && update(g.id, { caption: e.target.value })} />
              <select className={adminInput + " text-xs mb-2"} value={g.category} onChange={(e) => update(g.id, { category: e.target.value })}>{data.categories.map((c) => <option key={c}>{c}</option>)}</select>
              <div className="flex items-center justify-between text-xs">
                <button onClick={() => update(g.id, { published: !g.published })} className={g.published ? "text-gold flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"} data-testid={`gallery-publish-${g.id}`}>{g.published ? <Eye size={13} /> : <EyeOff size={13} />} {g.published ? "Published" : "Draft"}</button>
                <button onClick={() => update(g.id, { featured: !g.featured })} title="Feature" className={g.featured ? "text-gold" : "text-muted-foreground"} data-testid={`gallery-feature-${g.id}`}><Star size={13} /></button>
                <button onClick={() => update(g.id, { archived: true })} title="Archive" className="text-muted-foreground hover:text-red-300"><Archive size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {imgs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No images in this view.</p>}
    </div>
  );
}
